import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  Users,
  BarChart3,
  Wrench,
  FileCode,
  PenLine,
  Radio,
  Layers,
  Tags,
  FileText,
  Mail,
  HelpCircle,
  Zap,
  Cloud,
  Workflow,
  Mic,
  Linkedin,
  Target,
  Lightbulb,
  Link2,
  ListTodo,
  Library,
  Boxes,
  Map,
  Bot,
  Globe,
  ClipboardList,
  PauseCircle,
  MessageSquare,
  Spline,
  QrCode,
  CreditCard,
  BrainCircuit,
  ScanSearch,
  UserCheck,
  Clock,
  FolderGit2,
} from 'lucide-react';

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  color: string;
}

export interface NavGroup {
  title: string;
  icon: LucideIcon;
  color: string;
  items: NavItem[];
}

/**
 * Grouped app navigation — shared by header dropdown and left sidebar.
 * Mirrors Engine dashboard sections so the two stay in sync.
 * Documentation = send pipeline, open/click guides, Inngest jobs, edge functions; MCP = connector memory; Tools = Postmark, automations.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Newsletter',
    icon: BookOpen,
    color: 'text-sky-500',
    items: [
      { path: '/outgoing-newsletters', label: 'Content', icon: Mail, color: 'text-emerald-500' },
      { path: '/narrative-arcs', label: 'Narrative Arcs', icon: Spline, color: 'text-blue-500' },
      { path: '/held-emails', label: 'Held emails', icon: PauseCircle, color: 'text-amber-500' },
      { path: '/channels', label: 'Channels', icon: Radio, color: 'text-indigo-500' },
      { path: '/subscriber-field-definitions', label: 'Fields', icon: Tags, color: 'text-violet-500' },
      { path: '/charts', label: 'Charts', icon: BarChart3, color: 'text-emerald-500' },
      { path: '/elements', label: 'Elements', icon: Layers, color: 'text-sky-500' },
    ],
  },
  {
    title: 'MCP',
    icon: Bot,
    color: 'text-violet-500',
    items: [
      {
        path: '/agent-directives',
        label: 'Configuration & memory',
        icon: Bot,
        color: 'text-violet-500',
      },
    ],
  },
  {
    title: 'Documentation',
    icon: Library,
    color: 'text-teal-500',
    items: [
      { path: '/guide/email-pipeline', label: 'Send pipeline guide', icon: HelpCircle, color: 'text-teal-500' },
      { path: '/guide/email-tracking', label: 'Open/click tracking', icon: BarChart3, color: 'text-sky-500' },
      { path: '/guide/inngest', label: 'Inngest jobs', icon: Zap, color: 'text-violet-500' },
      { path: '/guide/edge-functions', label: 'Edge functions', icon: Cloud, color: 'text-orange-500' },
      { path: '/system-map', label: 'System map', icon: Map, color: 'text-emerald-500' },
    ],
  },
  {
    title: 'Tools',
    icon: Wrench,
    color: 'text-orange-500',
    items: [
      { path: '/cdn', label: 'CDN Assets', icon: Cloud, color: 'text-sky-500' },
      { path: '/go', label: 'Go links', icon: QrCode, color: 'text-emerald-500' },
      { path: '/postmark-templates', label: 'Postmark Tpls', icon: FileCode, color: 'text-emerald-500' },
      { path: '/postmark-signatures', label: 'Postmark Sigs', icon: PenLine, color: 'text-emerald-500' },
      { path: '/automations', label: 'Automations', icon: Workflow, color: 'text-emerald-500' },
    ],
  },
  {
    /** CTO-as-a-Service — the mycto.stephansmith.me connector, not the Engine MCP. */
    title: 'My AI CTO',
    icon: BrainCircuit,
    color: 'text-fuchsia-500',
    items: [
      { path: '/cto/waitlist', label: 'Waitlist', icon: Clock, color: 'text-amber-500' },
      { path: '/cto/members', label: 'Members', icon: UserCheck, color: 'text-emerald-500' },
      { path: '/cto/reviews', label: 'Reviews', icon: ScanSearch, color: 'text-fuchsia-500' },
      { path: '/cto/codebases', label: 'Codebases', icon: FolderGit2, color: 'text-fuchsia-500' },
      { path: '/cto/frameworks', label: 'Frameworks', icon: Library, color: 'text-fuchsia-500' },
      { path: '/membership-tiers', label: 'Membership tiers', icon: CreditCard, color: 'text-emerald-500' },
    ],
  },
  {
    title: 'Core',
    icon: BarChart3,
    color: 'text-emerald-500',
    items: [
      { path: '/subscribers', label: 'Connections', icon: Users, color: 'text-emerald-500' },
      { path: '/connections', label: 'People (profiles)', icon: Users, color: 'text-violet-500' },
      { path: '/site-analytics', label: 'Site analytics', icon: Globe, color: 'text-sky-500' },
      { path: '/tasks', label: 'Networking tasks', icon: ListTodo, color: 'text-amber-500' },
      { path: '/transcripts', label: 'Transcripts', icon: Mic, color: 'text-emerald-500' },
      {
        path: '/writing-chat',
        label: 'Writing chat',
        icon: MessageSquare,
        color: 'text-emerald-500',
      },
      { path: '/linkedin', label: 'LinkedIn', icon: Linkedin, color: 'text-[#0A66C2]' },
      { path: '/newsletters', label: 'Reference Materials', icon: BookOpen, color: 'text-yellow-500' },
    ],
  },
  {
    title: 'More',
    icon: FileCode,
    color: 'text-violet-500',
    items: [
      { path: '/goals', label: 'Goals', icon: Target, color: 'text-emerald-500' },
      { path: '/thoughts', label: 'Thoughts', icon: Lightbulb, color: 'text-purple-500' },
      { path: '/references', label: 'LI Posts', icon: Link2, color: 'text-blue-500' },
      { path: '/articles', label: 'Posts', icon: BookOpen, color: 'text-sky-500' },
    ],
  },
  {
    title: 'Misc',
    icon: Boxes,
    color: 'text-gray-500',
    items: [
      { path: '/templates', label: 'Templates', icon: FileText, color: 'text-violet-500' },
      {
        path: '/survey',
        label: 'Fractional peers',
        icon: ClipboardList,
        color: 'text-amber-500',
      },
    ],
  },
];

/** Match nav item for detail routes (e.g. /subscribers/:id). */
export function isNavPathActive(pathname: string, navPath: string): boolean {
  if (pathname === navPath) return true;
  if (navPath !== '/' && pathname.startsWith(`${navPath}/`)) return true;
  return false;
}

export function getAllNavItems(): NavItem[] {
  return NAV_GROUPS.flatMap((group) => group.items);
}

export function getNavItemByPath(path: string): NavItem | undefined {
  return getAllNavItems().find((item) => item.path === path);
}

/** Resolve the current URL to the best-matching sidebar nav item. */
export function findNavItemForPathname(pathname: string): NavItem | null {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (isNavPathActive(pathname, item.path)) {
        return item;
      }
    }
  }
  return null;
}

export function searchNavItems(query: string): NavItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return getAllNavItems().filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.path.toLowerCase().includes(q)
  );
}
